/**
 * Where the editor's windows are, and which of them are open.
 *
 * Kept in the browser rather than on the server: how somebody arranges their
 * screen is a fact about their screen, not about the world. It survives a reload
 * because rearranging six panels twice a day is nobody's idea of building a game.
 */

import type { WindowRect } from './Window';

export type WindowName =
  'tools' | 'content' | 'details' | 'world' | 'map' | 'keys' | 'make' | 'talk' | 'item' | 'quests';

export type Layout = Record<WindowName, WindowRect>;

/**
 * Where each window starts.
 *
 * The tools go across the top under the bar, a row of tools over a row of that
 * tool's own options, which is the arrangement every editor of this kind has
 * settled on. Everything else is down the two sides: what is being worked with
 * on the left, what is in the world on the right, and the middle left clear for
 * the world itself.
 */
export function defaultLayout(): Layout {
  const width = typeof window === 'undefined' ? 1600 : window.innerWidth;
  const height = typeof window === 'undefined' ? 900 : window.innerHeight;

  const right = Math.max(320, width - 300);

  // Below the bar and below the line that says what is under the cursor, which
  // sits in the same corner and was being covered by the tools.
  const bar = 148;

  // And below the tools, which now run across rather than down.
  const top = bar + 108;

  return {
    // Only as wide as the tools in it: the window sizes itself, and this is
    // where it starts rather than how big it is.
    tools: { x: 12, y: bar, width: 560, height: 96 },
    content: { x: 12, y: top, width: 250, height: Math.max(240, height - top - 40) },
    // Under the outliner rather than across its bottom edge: the details window
    // appears on its own the moment anything is held, so it never goes through
    // the step that finds it somewhere clear.
    details: { x: right, y: top + 302, width: 280, height: Math.max(280, height - top - 322) },
    world: { x: right, y: top, width: 280, height: 290 },
    map: { x: right - 300, y: top, width: 280, height: 420, folded: true },
    // Its own lane between the tools and the panels down the right, since it is
    // read beside whatever is being worked in rather than instead of it.
    keys: { x: 280, y: top, width: 260, height: 420 },
    make: { x: 560, y: top, width: 340, height: Math.max(380, height - top - 40) },
    // A canvas wants room. Big by default, and out of the way of the panels
    // down both sides, since it is opened to be worked in rather than glanced at.
    talk: {
      x: 300,
      y: top,
      width: Math.max(640, width - 620),
      height: Math.max(420, height - top - 60),
    },
    // The same room a conversation gets, and for the same reason: a quest is
    // read as a shape, and a shape needs somewhere to be a shape in. It used to
    // be a tab in the Create window, three hundred and forty pixels wide, which
    // is a column rather than a canvas.
    quests: {
      x: 300,
      y: top,
      width: Math.max(620, width - 640),
      height: Math.max(420, height - top - 60),
    },
    // Beside the list it was opened from rather than over it: the point of a
    // window of its own is being able to read one item while writing another.
    item: {
      x: 920,
      y: top,
      width: 340,
      height: Math.max(420, height - top - 60),
    },
  };
}

/** Which windows are open when nobody has said otherwise. */
export const DEFAULT_OPEN: WindowName[] = ['tools', 'world'];

const LAYOUT_KEY = 'inverse.editor.windows';

/**
 * Which screen an arrangement belongs to.
 *
 * The same reason the game's own windows are remembered per screen: an editor
 * laid out on a wide monitor is a heap of overlapping panels on a laptop, and
 * being handed the wrong one is worse than being handed none.
 */
function screenKey(): string {
  return `@${Math.round(window.innerWidth / 100)}x${Math.round(window.innerHeight / 100)}`;
}

interface Stored {
  layout: Partial<Layout>;
  open: WindowName[];
  /** Whether the tools are a row of buttons rather than a list with the words. */
  compact?: boolean;
}

export function loadLayout(): { layout: Layout; open: Set<WindowName>; compact: boolean } {
  const layout = defaultLayout();

  try {
    const held =
      window.localStorage.getItem(LAYOUT_KEY + screenKey()) ??
      window.localStorage.getItem(LAYOUT_KEY);
    if (!held) return { layout, open: new Set(DEFAULT_OPEN), compact: false };

    const parsed = JSON.parse(held) as Partial<Stored>;

    for (const [name, rect] of Object.entries(parsed.layout ?? {})) {
      const known = layout[name as WindowName];
      if (!known || typeof rect !== 'object') continue;

      // Anything off the edge of a screen that is no longer the same size comes
      // back to somewhere it can be reached.
      layout[name as WindowName] = {
        x: Math.min(Math.max(rect.x ?? known.x, 0), Math.max(0, window.innerWidth - 80)),
        y: Math.min(Math.max(rect.y ?? known.y, 0), Math.max(0, window.innerHeight - 40)),
        width: Math.max(180, Math.min(rect.width ?? known.width, 2400)),
        height: Math.max(90, Math.min(rect.height ?? known.height, 1600)),
        folded: rect.folded === true,
      };
    }

    const open = Array.isArray(parsed.open) ? parsed.open : DEFAULT_OPEN;
    return { layout, open: new Set(open), compact: parsed.compact === true };
  } catch {
    return { layout, open: new Set(DEFAULT_OPEN), compact: false };
  }
}

/**
 * The same window, not rolled up.
 *
 * A window remembers being folded, and the map is folded to begin with, so
 * pressing Map on the bar produced a title bar with nothing under it. Rolling
 * one up is a thing somebody does to a window they can see; opening one is a
 * request to see it.
 */
export function unrolled(rect: WindowRect): WindowRect {
  return rect.folded === true ? { ...rect, folded: false } : rect;
}

/**
 * Somewhere to put a window that is not on top of one already there.
 *
 * Two windows opened from the bar used to land in the same place and the second
 * hid the first, which reads as the button not having worked.
 *
 * Judged on how much ground they actually share, and in both directions. It was
 * judged on where their top left corners were, so two windows two hundred pixels
 * apart counted as clear of each other while one covered nine tenths of the
 * other; and it only asked whether the window being opened was buried, so a wide
 * one could open straight over three narrow ones and bury all of them.
 *
 * The search is a sweep across the screen rather than a diagonal cascade. A
 * cascade steps out of the way of a small window in two or three moves and never
 * escapes a large one, because every step is still inside it.
 */
export function clearOf(
  layout: Layout,
  name: WindowName,
  open: ReadonlySet<WindowName>,
): WindowRect {
  const wanted = layout[name];
  const others = [...open].filter((held) => held !== name).map((held) => layout[held]);
  if (others.length === 0) return wanted;

  /** How much ground two of them share. */
  const shared = (one: WindowRect, other: WindowRect): number => {
    const across = Math.min(one.x + one.width, other.x + other.width) - Math.max(one.x, other.x);
    const down = Math.min(one.y + one.height, other.y + other.height) - Math.max(one.y, other.y);
    return across > 0 && down > 0 ? across * down : 0;
  };

  /** Buried, or burying: either is a window somebody cannot see. */
  const clashes = (rect: WindowRect): boolean => {
    const mine = Math.max(1, rect.width * rect.height);
    return others.some((other) => {
      const over = shared(rect, other);
      return over > mine * 0.4 || over > Math.max(1, other.width * other.height) * 0.4;
    });
  };

  if (!clashes(wanted)) return wanted;

  const room =
    typeof window === 'undefined'
      ? { width: 1600, height: 900 }
      : { width: window.innerWidth, height: window.innerHeight };

  // Swept from where it was asked for outwards, so a window that has to move
  // moves as little as it can and stays near the place it was meant to be.
  const step = 40;
  const spots: WindowRect[] = [];
  for (let y = wanted.y; y + wanted.height <= room.height - 8; y += step) {
    for (let x = 8; x + wanted.width <= room.width - 8; x += step) {
      spots.push({ ...wanted, x, y });
    }
  }

  spots.sort(
    (one, other) =>
      Math.hypot(one.x - wanted.x, one.y - wanted.y) -
      Math.hypot(other.x - wanted.x, other.y - wanted.y),
  );

  /** How much of what is already open a spot would cover. */
  const cost = (rect: WindowRect): number =>
    others.reduce((total, other) => total + shared(rect, other), 0);

  // The nearest spot that covers nothing, or failing that the one that covers
  // least. A canvas wanting most of the screen with three panels already up has
  // nowhere clear to go, and the old answer was to open exactly where it was
  // asked for and bury two of them whole. Covering the edge of one is better
  // than covering the whole of two, and it is always at least as good as not
  // looking.
  let best = wanted;
  let least = cost(wanted);

  for (const spot of spots) {
    const held = cost(spot);
    if (held < least) {
      least = held;
      best = spot;
      if (held === 0) break;
    }
  }

  return best;
}

export function saveLayout(layout: Layout, open: Set<WindowName>, compact: boolean): void {
  try {
    window.localStorage.setItem(
      LAYOUT_KEY + screenKey(),
      JSON.stringify({ layout, open: [...open], compact } satisfies Stored),
    );
  } catch {
    // Storage refused. The arrangement lasts as long as the page does.
  }
}
